import { useMutation, useQueryClient } from '@tanstack/react-query';
import { omit } from 'lodash';
import { ArrowRightLeftIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import type { AssetDetailType } from '@/common/asset';
import { DeleteConfirmDialog } from '@/components/soft-delete/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { useAssetReadOnly } from '@/context/AssetReadOnlyContext';
import { usePermissionMetadata } from '@/hooks/use-permission';
import { useSoftDeleteItem } from '@/hooks/use-soft-delete';
import { addAsset } from '@/queries/assets/add-asset';
import { updateAsset } from '@/queries/assets/update-asset';
import { apiErrorMessage, extractApiError } from '@/queries/common';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import { PRESIGNED_UPLOAD_SCOPE, uploadFileWithPresignedUrl } from '@/queries/uploads/presigned-upload';
import { AssetForm } from './asset-form';
import { MoveAssetDialog } from './move-asset-dialog';

export type AssetOverviewProps = {
	asset: AssetDetailType | null;
	isNew?: boolean;
	assetName: string;
	/** Restore of a deleted item previewed read-only (GH #331), where Delete sits on a live one */
	restoreButton?: React.ReactNode;
};

export function AssetOverview({ asset, isNew, assetName, restoreButton }: AssetOverviewProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
	const [stagedBannerFile, setStagedBannerFile] = useState<File | null>(null);
	const { isSuperAdmin, organizations } = usePermissionMetadata();
	const { isReadOnly } = useAssetReadOnly();

	const urlOrgId = searchParams.get('orgid');
	// A new asset goes to the organisation from the URL or the user's first one
	const targetOrgId = asset?.organizationId || urlOrgId || organizations?.[0]?.id || undefined;

	// A coded backend error (e.g. an invalid responsible specialist) says what went wrong;
	// anything else keeps the generic message.
	const saveErrorMessage = (error: unknown, fallback: string) =>
		extractApiError(error).code ? apiErrorMessage(error) : fallback;

	const addNewAssetApi = useMutation({
		mutationFn: async (data: Partial<AssetDetailType>) => {
			const orgId = urlOrgId || organizations?.[0]?.id || '';
			const mappedData: Partial<AssetDetailType> = {
				...data,
				name: assetName || t('assets.form.unnamedAssetDefault'),
				// Strip the local object URL — imageUrl will be set after banner upload below
				imageUrl: '',
			};

			// If an organization ID is provided in the URL or from the user's organizations, include it in the asset data
			if (orgId) {
				mappedData.organizationId = orgId;
			}

			// API call to add new asset
			const newAsset = await addAsset(omit(mappedData, ['id']));

			if (!newAsset?.id) {
				throw new Error(t('assets.toast.newAssetIdMissing'));
			}

			// If the user picked a banner, upload it now that we have the asset ID
			if (stagedBannerFile) {
				const { key } = await uploadFileWithPresignedUrl(stagedBannerFile, {
					assetId: newAsset.id,
					scope: PRESIGNED_UPLOAD_SCOPE.BANNER,
				});
				await updateAsset(newAsset.id, omit({ ...mappedData, imageUrl: key }, ['id']));
			}
			return newAsset;
		},
		onError(error) {
			console.log('Add asset error:', error);
			toast.error(saveErrorMessage(error, t('assets.toast.addError')));
		},
		onSuccess(data) {
			console.log('Add asset successful:', data);
			setStagedBannerFile(null);
			toast.success(t('assets.toast.addSuccess'));
			navigate(data?.id ? `/?selected=${data.id}` : '/');
		},
	});

	const updateAssetApi = useMutation({
		mutationFn: async ({ id, data }: { id: string; data: Partial<AssetDetailType> }) => {
			if (!id) {
				throw new Error(t('assets.toast.assetIdRequiredForUpdate'));
			}
			// API call to update existing asset
			console.log('Updating asset with data:', asset?.id, data);
			const mappedData: Partial<AssetDetailType> = {
				...data,
				name: assetName || t('assets.form.unnamedAssetDefault'),
			};
			return updateAsset(id, omit(mappedData, ['id']));
		},
		onError(error) {
			console.log('Update asset error:', error);
			toast.error(saveErrorMessage(error, t('assets.toast.updateError')));
		},
		onSuccess(data, { id }) {
			console.log('Update asset successful:', data);
			toast.success(t('assets.toast.updated'));
			// The form remounts from the cached asset; a stale copy would send the old link on the next save
			queryClient.invalidateQueries({ queryKey: ['assets', id] });
		},
	});

	// Delete with cascade (GH #331): the dialog shows what goes with the asset; the toast offers Undo
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	const deleteAssetApi = useSoftDeleteItem({
		onDeleted: () => {
			setIsDeleteOpen(false);
			navigate('/');
		},
	});

	if (!asset && !isNew) {
		return <div>{t('assets.detail.noData')}</div>;
	}

	const onSubmit = (data: Partial<AssetDetailType>) => {
		// Update existing asset
		if (!isNew && asset?.id) {
			updateAssetApi.mutate({
				id: asset.id,
				data,
			});
			return;
		}
		// Add new asset
		if (isNew) {
			addNewAssetApi.mutate(data);
			return;
		}
		// This should not happen
		console.error('Invalid state: onSubmit called without valid asset or isNew flag.');
	};

	const handleDelete = () => {
		if (asset?.id) {
			setIsDeleteOpen(true);
		}
	};
	const deleteName = asset?.name || t('common.entities.asset', { count: 1 });

	return (
		<>
			<AssetForm
				asset={asset}
				onSubmit={onSubmit}
				isSubmitting={addNewAssetApi.isPending || updateAssetApi.isPending || deleteAssetApi.isPending}
				onDelete={handleDelete}
				onBannerFileStaged={setStagedBannerFile}
				organizationId={targetOrgId}
				restoreButton={restoreButton}
			/>
			{asset?.id && (
				<DeleteConfirmDialog
					open={isDeleteOpen}
					onOpenChange={setIsDeleteOpen}
					target={softDeleteTargets.asset(asset.id)}
					name={deleteName}
					isDeleting={deleteAssetApi.isPending}
					onConfirm={({ acceptWorkPackageImpact }) =>
						deleteAssetApi.mutate({
							target: softDeleteTargets.asset(asset.id as string),
							name: deleteName,
							acceptWorkPackageImpact,
							successMessage: t('assets.toast.deleteSuccess'),
						})
					}
				/>
			)}
			{asset && !isNew && isSuperAdmin && !isReadOnly && (
				<div className="flex items-center gap-2 mb-4 mt-4">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setIsMoveDialogOpen(true)}
						className="h-7 lg:h-8 text-xs lg:text-sm px-2 lg:px-3 bg-white"
					>
						<ArrowRightLeftIcon className="mr-1 size-3.5 lg:size-4" /> {t('common.actions.move')}
					</Button>
				</div>
			)}

			{asset && !isNew && (
				<MoveAssetDialog
					isOpen={isMoveDialogOpen}
					onClose={() => setIsMoveDialogOpen(false)}
					assetId={asset.id as string}
					currentOrgId={asset.organizationId as string}
					assetTitle={asset.name || t('assets.form.unnamedAssetFallback')}
					onMoveSuccess={() => {
						toast.success(t('assets.toast.moveSuccess'));
						navigate(`/?selected=${asset.id}`);
					}}
				/>
			)}
		</>
	);
}
